import { useState, useEffect } from "react";
import { message } from "antd";
import db from "../../../../database";
import { getCustomerWallet, getStockForProductService } from "../../../../services";
import { getApplicablePrice } from "../../../../utils/getPriceList";
import { shouldUseBatchPrice } from "../../../../utils/resolvePricingPolicy";
import useCartStore from "../../../../stores/useCartStore";
import messages from "../../../../errors/messages.en-IN.json";

/**
 * useWalletAndBatches
 *
 * Manages:
 *  - Customer wallet data fetching + display state
 *  - Online batch fetching for products that need live stock data
 *  - Gift card items and "add to bag" products from the product DB
 *  - kiosk/layout type state driven by customStorageChange events
 */
const useWalletAndBatches = ({
  cart,
  tillData,
  posConfig,
  batchSetAvailable,
  setDisplayBatchSelection,
  selectProductToCart,
  setProductStock,
  setGiftCardItems,
  kioskUI: kioskUIProp,
  setKioskUI: setKioskUIProp,
  layoutType: layoutTypeProp,
  setLayoutType: setLayoutTypeProp,
}) => {
  const [showCustomerWallet, setShowCustomerWallet] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState([]);
  const [onlineBatches, setOnlineBatches] = useState([]);
  const [addToBagProducts, setAddToBagProducts] = useState([]);
  const [addToBagFlag, setAddToBagFlag] = useState(false);
  const [tillLink, setTillLink] = useState(false);
  const [isWalletFlag, setIswalletFlag] = useState(false);
  const [isRefundWalletOpen, setIsRefundWalletOpen] = useState(false);

  const getFinalPayableAmount = () => {
    const { cart: storeCart, exchangeCart } = useCartStore.getState();
    const cartTotal = parseFloat(storeCart?.total || 0);
    const exchangeTotal = parseFloat(exchangeCart?.total || 0);
    return cartTotal + exchangeTotal;
  };

  // Don't shadow `fetchWalletData` from the closure with a parameter — callers
  // (e.g. SMPaymentBar) invoke walletFunc() with no args, so the shadowed
  // param was undefined and the wallet balance fetch silently crashed.
  // (Issue #12)
  const walletFunc = async () => {
    const finalAmount = getFinalPayableAmount();
    if (finalAmount <= 0) {
      setIsRefundWalletOpen(true);
      return;
    }
    setIswalletFlag(true);
    await fetchWalletData(true);
  };

  const fetchWalletData = async (flag) => {
    try {
      const wallets = await getCustomerWallet(cart?.customer?.cwrCustomerId);

      const defaultCustomerData = {
        id: cart.customer.code || "",
        name: cart.customer.name || "",
        phone: cart.customer.mobileNo || "",
        email: cart.customer.email || null,
        currentBalance: 0,
        totalSpent: 0,
        transactions: [],
      };

      const mappedData =
        wallets?.wallets?.length > 0
          ? wallets.wallets.map((wallet) => ({
              id: cart.customer.code || "",
              name: cart.customer.name || "",
              phone: cart.customer.mobileNo || "",
              email: cart.customer.email || null,
              currentBalance: wallet.balance_amt || 0,
              totalSpent: wallet.deposit_amt || 0,
              joinDate: wallet.created || new Date().toISOString().split("T")[0],
              lastTransaction: wallet.laxt_trxdate || new Date().toISOString().split("T")[0],
              transactions: (wallet.transactions || []).map((trx) => {
                let type = "Debit";
                let description = "Purchase Payment";

                if (trx.trx_type === "DP") {
                  type = "Credit";
                  description = "Wallet Top-up";
                }
                if (trx.trx_type === "REF") {
                  type = "Credit";
                  description = "Wallet Refund";
                }
                if (trx.trx_type === "WD") {
                  type = "Debit";
                  description = "Purchase Payment";
                }

                return {
                  id: trx.cwr_wallet_trx_id,
                  date: trx.created.split(" ")[0],
                  time: new Date(trx.created).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  }),
                  type,
                  amount: Math.abs(trx.trx_value),
                  description,
                  reference: trx.cwr_wallet_trx_id,
                  balance: trx.closing_amt ?? wallet.balance_amt ?? 0,
                };
              }),
            }))
          : [defaultCustomerData];

      setSelectedCustomer(mappedData);
      setCustomersData(mappedData);
      if (!flag) {
        setShowCustomerWallet(true);
      }
      setTillLink(false);
    } catch (error) {
      message.error(`${messages.errors.customer.wallet_load_failed}`);
    }
  };

  const fetchOnlineBatches = async (productSet) => {
    const batches = Array.isArray(productSet) && productSet.length > 0 ? productSet : batchSetAvailable;
    if (!Array.isArray(batches) || batches.length === 0) return;

    const bUnitId = tillData?.tillAccess?.csBunit?.csBunitId;
    const productId = batches[0]?.productId;

    try {
      const payload = {
        query: `
          query {
            getStockForProduct(
              bUnitId: "${bUnitId}",
              productId: "${productId}"
            )
          }
        `,
      };

      const response = await getStockForProductService(payload);
      let raw = response?.data?.data?.getStockForProduct;
      if (!raw) return;

      raw = raw.replace(/([0-9]{4}-[0-9]{2}-[0-9]{2} [0-9:.]+)/g, '"$1"');
      const parsed = JSON.parse(raw);
      const receiptlessReturn2Enabled = posConfig?.enableReceiptlessReturn2 === "Y";
      const returnEnabled = receiptlessReturn2Enabled && cart?.isReturn === true && cart?.isBlindReceipt === true;

      if (!Array.isArray(parsed?.batches) || parsed.batches.length === 0) {
        console.debug("No batches returned from API for product");
        const base = batches[0];
        if (returnEnabled) {
          selectProductToCart(base, 1);
        } else {
          setProductStock(true);
        }
        return;
      }

      const base = batches[0];
      const applicablePrice = getApplicablePrice({
        priceList: base?.priceList,
        selectedUomId: base?.uomData?.[0]?.csUomId,
        sPriceListId: cart?.customer?.sPriceListId,
        alternateUom: base?.alternateUom || [],
        primaryUomId: base?.uom || null,
      });
      const unitPrice = applicablePrice?.pricestd ?? applicablePrice?.pricelist ?? base?.sunitprice ?? 0;
      const useBatch = shouldUseBatchPrice(base);

      const mapped = parsed.batches.map((b) => ({
        ...base,
        mBatchId: b?.m_batch_id,
        batchno: b?.batchno,
        salePrice: useBatch ? (b?.listprice ?? unitPrice) : unitPrice,
        realPrice: useBatch ? (b?.listprice ?? unitPrice) : unitPrice,
        mrpPrice: base.listPrice ?? 0,
        stock: b?.qtyonhand,
        upc: b?.batchupc,
        listPrice: b?.listprice,
        purchasePrice: b?.purchaseprice,
        purchaseDate: b?.purchase_date || b?.startdate,
      }));

      const inStockBatches = mapped.filter((b) => b.stock > 0);

      if (inStockBatches.length === 0) {
        setOnlineBatches([]);
        console.debug("All batches are out of stock for product");
        setProductStock(true);
        return;
      }

      if (inStockBatches.length === 1) {
        selectProductToCart(inStockBatches[0], 1);
        return;
      }

      setOnlineBatches(inStockBatches);
      setDisplayBatchSelection(true);
    } catch (err) {
      console.error("Batch fetch error:", err);
    }
  };

  // Load gift card items and add-to-bag products; listen for kiosk/layout storage changes
  useEffect(() => {
    const handleCustomEvent = (event) => {
      const dynamoDBValue = event.detail.newValue;
      if (event.detail.key === "kioskUI") {
        setKioskUIProp(dynamoDBValue ? dynamoDBValue : parseFloat(localStorage.getItem("kioskUI")) || 0);
      } else if (event.detail.key === "layoutType") {
        setLayoutTypeProp(dynamoDBValue ? dynamoDBValue : parseFloat(localStorage.getItem("layoutType")) || 0);
      }
    };

    db.products
      .filter((product) => product.isactive == true)
      .toArray()
      .then((productsFetched) => {
        let items = [];
        let bagItems = [];
        productsFetched.map(async (item) => {
          if (item.productSegment === "GC") {
            const giftCardData = await db.giftCardData.toArray();
            const matchingGiftCard = giftCardData.filter((giftcard) => giftcard.mProductId === item.mProductId);
            item.matchingGiftCard = matchingGiftCard;
            items.push(item);
          } else if (item.productSegment === "BG") {
            bagItems.push(item);
          }
        });
        setAddToBagProducts(bagItems);
        setGiftCardItems(items);
      });

    window.addEventListener("customStorageChange", handleCustomEvent);
    return () => {
      window.removeEventListener("customStorageChange", handleCustomEvent);
    };
  }, []);

  return {
    showCustomerWallet,
    setShowCustomerWallet,
    customersData,
    setCustomersData,
    selectedCustomer,
    setSelectedCustomer,
    onlineBatches,
    setOnlineBatches,
    addToBagProducts,
    setAddToBagProducts,
    addToBagFlag,
    setAddToBagFlag,
    tillLink,
    setTillLink,
    isWalletFlag,
    setIswalletFlag,
    isRefundWalletOpen,
    setIsRefundWalletOpen,
    walletFunc,
    fetchWalletData,
    fetchOnlineBatches,
    getFinalPayableAmount,
  };
};

export default useWalletAndBatches;
